import type { EmptyApiResponse } from '../shared';
import type { SbrResponseDataResponse } from './sbr';

/**
 * Represents the various processing statuses that a CVT file upload can be in during its lifecycle.
 */
export enum CvtFileProcessingStatus {
    /** Unconfirmed but should be deprecated as this was used in V1 */
    CVT_FILE_UPLOAD_FAIL = 'CVT_FILE_UPLOAD_FAIL',

    /** Unconfirmed but should be deprecated as this was used in V1 */
    CVT_FILE_UPLOADED = 'CVT_FILE_UPLOADED',

    /** Unconfirmed but should be deprecated as this was used in V1 */
    CVT_FILE_UPLOADING = 'CVT_FILE_UPLOADING',

    /** Error occurred while processing diagnostic data or duplicate data was detected. */
    ERROR_PROCESSING_DIAGNOSTIC_DATA = 'ERROR_PROCESSING_DIAGNOSTIC_DATA',

    /** The file has finished processing and a response has been received from SBR. */
    RECEIVED_RESPONSE_FROM_SBR = 'RECEIVED_RESPONSE_FROM_SBR',

    /** The SBR process failed. */
    SBR_PROCESS_FAILED = 'SBR_PROCESS_FAILED',

    /** Both presigned URL uploads to SBR and STS failed. (Deprecated as STS failures are handled by endpoint /cvt/upload/{diagnosticId}) */
    SBR_STS_UPLOAD_FAIL = 'SBR_STS_UPLOAD_FAIL',

    /** The CVT file upload to SBR's presigned URL failed. Batch job will handle retrying the upload if STS succeeded. */
    SBR_UPLOAD_FAIL = 'SBR_UPLOAD_FAIL',

    /** The CVT file has been sent to SBR for processing. (Status updated from /cvt/send/v2/{stsDiagnosticId}/{sbrDiagnosticId}) */
    SENT_CVT_TO_SBR = 'SENT_CVT_TO_SBR',
    /** The CVT file upload to STS's presigned URL failed. (Deprecated as STS failures are handled by endpoint /cvt/upload/{diagnosticId}) */

    STS_UPLOAD_FAIL = 'STS_UPLOAD_FAIL',

    /** Unconfirmed but should be deprecated as this was used in V1 */
    UPLOAD_SUCCESS = 'UPLOAD_SUCCESS',
}

/**
 * Represents the failure status of a CVT file upload to SBR's presigned URL.
 */
export enum CvtUploadFailStatus {
    SBR = 'SBR_UPLOAD_FAIL',
    STS = 'STS_UPLOAD_FAIL',
}

/**
 * Represents the failure status of a CVT file upload to SBR's presigned URL.
 */
export type CvtSbrUploadFailureStatus = {
    /** The failure status of the CVT file upload to SBR's presigned URL, if it failed. */
    sbrFailStatus?: CvtUploadFailStatus.SBR;

    /** An optional message providing additional details about the failure of the CVT file upload to SBR's presigned URL. */
    sbrFailMessage?: string;
};

/**
 * Represents the failure status of a CVT file upload to STS's presigned URL.
 */
export type CvtStsUploadFailureStatus = {
    /** The failure status of the CVT file upload to STS's presigned URL, if it failed. */
    stsFailStatus?: CvtUploadFailStatus.STS;

    /** An optional message providing additional details about the failure of the CVT file upload to STS's presigned URL. */
    stsFailMessage?: string;
};

/**
 * Request DTO for retrieving the CVT diagnostic details for a given diagnostic ID.
 *
 * Endpoint: GET /cvt/status/v2/{diagnosticId}
 */
export type GetCvtDiagnosticDetailsRequest = {
    /** The unique identifier of the diagnostic for which to retrieve CVT report details. (stsDiagnosticId) */
    diagnosticId: string;
};

/**
 * Response DTO for retrieving the CVT diagnostic details for a given diagnostic ID.
 *
 * The response includes the processing status of the CVT file upload and SBR's response after processing the CVT file.
 * It excludes graph data, which can be retrieved via a separate endpoint using the sbrResponseDataUrl provided in this response.
 *
 *  Endpoint: GET /cvt/status/v2/{diagnosticId}
 */
export type GetCvtDiagnosticDetailsResponse = {
    /** The account ID of the account associated with the CVT diagnostic. */
    accountId: string;

    /** The name of the account associated with the CVT diagnostic. */
    accountName: string;

    /** The unique identifier of the diagnostic for which CVT report details were retrieved (stsDiagnosticId). */
    diagnosticId: string;

    /** The name of the CVT file uploaded for the diagnostic. */
    fileName: string;

    /** The timestamp when the CVT file processing was completed. */
    jobCompletedTimestamp?: string;

    /** The timestamp when the CVT file was uploaded. */
    jobSubmittedTimestamp: string;

    /** The retailer information associated with the CVT diagnostic. */
    retailer: {
        /** The code of the retailer associated with the CVT diagnostic. */
        code: string;

        /** The district of the retailer associated with the CVT diagnostic. */
        district?: string;

        /** The name of the retailer associated with the CVT diagnostic. */
        name: string;

        /** The region of the retailer associated with the CVT diagnostic. */
        region?: string;

        /** The role of the retailer associated with the CVT diagnostic. */
        role: string;

        /** The zone of the retailer associated with the CVT diagnostic. */
        zone?: string;
    };

    /** The repair order number associated with the CVT diagnostic. */
    roNumber: string;

    /** The unique identifier of the SBR diagnostic associated with the CVT diagnostic. */
    sbrDiagnosticId?: string;

    /** The response data from SBR after processing the CVT file. Excludes graph data, which can be retrieved via the sbrResponseDataUrl. */
    sbrResponseData?: SbrResponseDataResponse;

    /** The URL to retrieve the response data from SBR including the graph data. */
    sbrResponseDataUrl?: string;

    /** The Vehicle Identification Number (VIN) of the vehicle associated with the CVT diagnostic. */
    vin: string;
};

/**
 * The request body for uploading a CVT file to the STS backend, which will handle uploading the file to S3.
 * This is used as a fallback method of uploading the CVT file in the event that the presigned URL upload to STS fails.
 *
 * Endpoint: POST /cvt/upload/{diagnosticId}
 */
export type PostCvtFileUploadRequest = {
    /** The unique identifier of the diagnostic for which to upload the CVT file. (STS diagnostic ID) */
    diagnosticId: string;

    /** The CVT file to be uploaded. */
    file: FormData;
};

/**
 * The response body for a successful CVT file upload to the STS backend.
 */
export type PostCvtFileUploadResponse = {
    /** The unique identifier of the diagnostic for which the CVT file was uploaded (stsDiagnosticId). */
    diagnosticId: string;
};

/**
 * The request for running the CVT transmission to SBR after uploading the CVT file, which will trigger SBR to begin processing the CVT data.
 *
 * Endpoint: POST /cvt/send/v2/{stsDiagnosticId}/{sbrDiagnosticId}
 */
export type PostCvtSendRequest = {
    /** The unique identifier of the STS diagnostic for which to run the CVT transmission. */
    stsDiagnosticId: string;

    /** The unique identifier of the SBR diagnostic for which to run the CVT transmission. */
    sbrDiagnosticId: string;

    /** An object representing any failures that occurred during the pre-signed URL upload process for the SBR diagnostic. */
    uploadFailureStatus: CvtSbrUploadFailureStatus;
};

/** The response for running the CVT transmission to SBR. Then endpoint does not return any additional data. */
export type PostCvtSendResponse = EmptyApiResponse;

/**
 * The request for creating presigned URLs and diagnostic IDs for uploading a CVT file.
 *
 * Endpoint: POST /cvt/upload/v2/{vin}/{repairOrderNumber}
 */
export type PostCvtUploadUrlsRequest = {
    /** The Vehicle Identification Number (VIN) of the vehicle associated with the CVT. */
    vin: string;

    /** The repair order number associated with the CVT. */
    repairOrderNumber: string;
};

/**
 * The response body for creating presigned URLs and diagnostic IDs for uploading a CVT file.
 */
export type PostCvtUploadUrlsResponse = {
    /** The unique identifier for the SBR diagnostic created for this CVT. This ID is used to track the SBR diagnostic associated with the CVT. */
    sbrDiagnosticId: string;

    /** The pre-signed URL for uploading the CVT file to SBR. This URL is used by the client to upload the CVT file directly to SBR's storage. */
    sbrUploadUrl: string;

    /** The unique identifier for the STS diagnostic created for this CVT. This ID is used to track the STS diagnostic associated with the CVT. */
    stsDiagnosticId: string;

    /** The pre-signed URL for uploading the CVT file to STS. This URL is used by the client to upload the CVT file directly to STS's storage. */
    stsUploadUrl: string;
};
